"use client"

import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { cn } from "@/lib/utils"
import { ChevronRightIcon, XIcon, ArrowLeftTriangleIcon } from "@/components/fun-ui/icons"
import { getTouchpointGuides, HelpGuides, TouchpointItem } from "@/stores/slices/guideSlice"
import { useSidebarCollapsed } from "@/stores/localStore"

const LINK_TOPS = [234, 274, 314] as const

type HelpPopupProps = {
  onClose?: () => void | Promise<void>;
  helpCenterRef?: React.RefObject<HTMLDivElement | null>;
  item?: TouchpointItem | null;
}

export function HelpPopup({ onClose, helpCenterRef, item }: HelpPopupProps = {}) {
  const [isOpen, setIsOpen] = useState(false)
  const [arrowTop, setArrowTop] = useState<number>(0)
  const popupRef = useRef<HTMLDivElement>(null)
  const { collapsed } = useSidebarCollapsed()
  const prevCollapsedRef = useRef<boolean>(collapsed)

  const displayData = useMemo(() => {
    const touchpointGuides = getTouchpointGuides(item)
    if (touchpointGuides?.type === 'help') {
      return (touchpointGuides as HelpGuides).value
    }
    return null
  }, [item])

  useEffect(() => {
    if (getTouchpointGuides(item)?.type === 'help') {
      setIsOpen(true)
    }
  }, [item])

  // 计算箭头位置的函数
  const calculateArrowPosition = useCallback(() => {
    if (!helpCenterRef?.current || !popupRef.current) return

    const helpCenterRect = helpCenterRef.current.getBoundingClientRect()
    const popupRect = popupRef.current.getBoundingClientRect()
    // 计算帮助中心菜单项的中心点相对于弹窗的位置
    const helpCenterCenterY = helpCenterRect.top + helpCenterRect.height / 2
    const popupTop = popupRect.top
    const arrowPosition = helpCenterCenterY - popupTop
    setArrowTop(arrowPosition)
  }, [helpCenterRef])

  // 初始化时立即计算箭头位置，避免看到延迟
  // 然后在动画完成后再次计算一次，确保位置准确
  useEffect(() => {
    if (!isOpen || !helpCenterRef?.current || !popupRef.current) {
      return
    }

    // 立即计算一次，避免看到延迟
    calculateArrowPosition()
    prevCollapsedRef.current = collapsed

    // 弹窗动画时长是 300ms，延迟 350ms 后再次计算确保位置准确
    const timer = setTimeout(() => {
      calculateArrowPosition()
    }, 350)

    return () => {
      clearTimeout(timer)
    }
  }, [isOpen, helpCenterRef, calculateArrowPosition, collapsed])

  // collapsed 状态变化时，延迟计算等待 sidebar 动画完成
  useEffect(() => {
    if (!isOpen || !helpCenterRef?.current || !popupRef.current) {
      return
    }

    // 如果 collapsed 状态没有变化，不处理
    if (prevCollapsedRef.current === collapsed) {
      return
    }

    // collapsed 状态变化时，延迟计算等待 sidebar 动画完成
    // sidebar 动画时长：收起 100ms，展开 300ms，所以延迟 350ms 确保动画完成
    const timer = setTimeout(() => {
      calculateArrowPosition()
    }, 350)

    // 更新 prevCollapsedRef
    prevCollapsedRef.current = collapsed

    return () => {
      clearTimeout(timer)
    }
  }, [collapsed, isOpen, helpCenterRef, calculateArrowPosition])

  const handleClose = async () => {
    setIsOpen(false)
    await onClose?.()
  }

  // 如果没有数据或未打开，不渲染组件
  if (!isOpen || !displayData) {
    return null
  }

  const links = (displayData.links ?? []).slice(0, LINK_TOPS.length)
  const sections = displayData.sections?.length
    ? displayData.sections
    : displayData.subtitle || displayData.content
      ? [{
        title: displayData.subtitle,
        content: displayData.content,
      }]
      : []
  const primarySection = sections[0]
  const title = displayData.title?.trim()
  const icon = displayData.icon?.trim()
  const watermarkImage = displayData.watermarkImage?.trim()
  const rewardPrefix = displayData.reward?.prefix?.trim()
  const rewardHighlight = displayData.reward?.highlight?.trim()
  const rewardDescription = displayData.reward?.description?.trim()

  return (
    <div
      ref={popupRef}
      className={cn(
        "absolute left-full bottom-4 z-50",
        "h-[382px] max-h-[382px] min-h-[382px] w-[276px] min-w-[276px] max-w-[276px]",
        "box-border overflow-visible rounded-[8px] border border-border",
        "animate-in slide-in-from-left-5 fade-in-0 duration-300",
        "bg-[#F7F9FF]",
        "shadow-[0px_3.2px_8px_0px_rgba(0,0,0,0.08)]"
      )}
    >
      {/* 箭头图标 - 指向侧边栏的"帮助中心"菜单项 */}
      <div
        className={cn(
          "absolute -left-[7px]",
          "z-10",
          "flex items-center"
        )}
        style={{
          top: `${arrowTop}px`,
          transform: 'translateY(-50%)',
        }}
      >
        <ArrowLeftTriangleIcon size={7} style={{ height: '11px' }} />
      </div>

      {/* 关闭按钮 */}
      <div
        onClick={handleClose}
        className={cn(
          "absolute right-[13px] top-3 z-20",
          "flex items-center justify-center",
          "size-[13px]",
          "text-annotation-foreground hover:text-foreground",
          "cursor-pointer opacity-50 transition-colors hover:opacity-100"
        )}
        aria-label="关闭"
      >
        <XIcon className="size-[13px]" />
      </div>

      {/* 内容 */}
      <div className="relative h-full w-full">
        {watermarkImage ? (
          <div
            className="pointer-events-none absolute left-[128px] top-px h-[114px] w-[140px] bg-contain bg-center bg-no-repeat opacity-[0.06]"
            style={{ backgroundImage: `url("${watermarkImage}")` }}
            aria-hidden
          />
        ) : null}
        {icon ? (
          <div className="absolute left-5 top-4 h-[30px] w-[33px] text-[24px] leading-[30px]" aria-hidden>
            {icon}
          </div>
        ) : null}
        {title ? (
          <h2
            className={cn(
              "absolute top-[19px] h-[26px] whitespace-nowrap text-[21px] font-medium leading-[25.2px] text-foreground",
              icon ? "left-[54px] w-[154px]" : "left-[22px] w-[220px]"
            )}
          >
            {title}
          </h2>
        ) : null}

        {rewardPrefix || rewardHighlight ? (
          <p className="absolute left-[22px] top-[62px] h-[17px] w-[220px] whitespace-nowrap text-xs font-medium leading-[16.8px] text-foreground">
            {rewardPrefix}
            {rewardPrefix && rewardHighlight ? ' ' : null}
            {rewardHighlight ? <span className="text-primary">{rewardHighlight}</span> : null}
          </p>
        ) : null}
        {rewardDescription ? (
          <p className="absolute left-[22px] top-[83px] h-8 w-[237px] overflow-hidden text-xs font-normal leading-4 text-muted-foreground">
            {rewardDescription}
          </p>
        ) : null}

        {primarySection?.title ? (
          <p className="absolute left-[22px] top-[132px] h-[17px] w-[180px] whitespace-nowrap text-xs font-medium leading-[16.8px] text-foreground">
            {primarySection.title}
          </p>
        ) : null}
        {primarySection?.content ? (
          <p className="absolute left-[22px] top-[153px] h-16 w-[237px] overflow-hidden text-xs font-normal leading-4 text-muted-foreground">
            {primarySection.content}
          </p>
        ) : null}

        {links.map((link, index) => (
          <WelcomeLink
            key={index}
            href={link.url}
            label={link.label}
            className="absolute left-[22px]"
            style={{ top: LINK_TOPS[index] }}
            onClick={(e) => {
              if (link.url === "#") {
                e.preventDefault()
              }
            }}
          />
        ))}
      </div>
    </div>
  )
}

function WelcomeLink({
  href,
  label,
  className,
  style,
  onClick,
}: {
  href: string
  label: string
  className?: string
  style?: React.CSSProperties
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}) {
  return (
    <a
      href={href}
      style={style}
      onClick={onClick}
      className={cn(
        "flex h-8 w-[238px] items-center justify-between",
        "rounded border border-border bg-background px-3",
        "transition-colors cursor-pointer",
        "hover:bg-muted",
        "focus:outline-none",
        className
      )}
      target={href === "#" ? undefined : "_blank"}
      rel={href === "#" ? undefined : "noreferrer"}
    >
      <span className="text-xs font-normal leading-[17px] text-foreground">{label}</span>
      <ChevronRightIcon className="size-3 shrink-0 text-annotation-foreground" />
    </a>
  )
}
